import React from 'react';

interface ErrorBoundaryProps {
  name?: string;
  fallbackMessage?: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error(`[ErrorBoundary:${this.props.name || 'unknown'}]`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 rounded-lg m-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2 text-wmt-primary">
              Something went wrong
            </h3>
            <p className="text-sm mb-4 text-wmt-secondary">
              {this.props.fallbackMessage || 'This section encountered an error. Your data is safe.'}
            </p>
            <button
              onClick={this.handleReset}
              className="px-4 py-2 rounded text-sm font-medium alliance-btn-primary"
            >
              Try Again
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left text-xs text-wmt-muted">
                <summary>Error Details</summary>
                <pre className="mt-2 p-2 rounded overflow-auto bg-wmt-primary">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
